@if(data()['type'] == 'tracefit'){
<div class="viz-card">
  <div class="viz-head">
    <span class="viz-title">Trace-fit analysis</span>
    <span class="viz-head-right">
      <span class="viz-badge">{{ jobs().length }} job{{ jobs().length === 1 ? '' : 's' }}</span>
      <span class="viz-help-btn" nz-icon nzType="question-circle" nzTheme="outline" nz-popover
        nzPopoverTitle="About this chart" [nzPopoverContent]="helpTpl" nzPopoverTrigger="click"
        nzPopoverPlacement="bottomRight" nzPopoverOverlayClassName="viz-help-popover"
        title="How to read this chart"></span>
    </span>
  </div>

  <ng-template #helpTpl>
    <div class="viz-help">
      <p>
        This chart plots a <b>trace-fit metric</b> — how well each job's modelled
        data matches the observed seismic traces — so you can compare jobs and
        track how the fit evolves.
      </p>

      <p class="viz-help-heading">Data</p>
      <ul>
        <li><b>View</b> — <b>Iteration</b> plots the metric against inversion
          iteration (convergence over time); <b>Spatial</b> plots it across the
          survey by shot position.</li>
        <li><b>Profile</b> — which fit metric to show on the Y axis.</li>
      </ul>

      <p class="viz-help-heading">Spatial</p>
      <ul>
        <li><b>Axis</b> — plot along <b>Inline</b> or <b>Crossline</b>.</li>
        <li><b>Iteration</b> — which iteration's per-shot data to show (defaults to the latest).</li>
        <li><b>Window (m)</b> — shots are averaged within fixed-width bins along
          the axis, so dense, irregular shots read as a clean line.</li>
      </ul>

      <p class="viz-help-heading">Display</p>
      <ul>
        <li><b>Type</b> — render as a connected <b>Line</b> or discrete <b>Scatter</b> points.</li>
        <li><b>Y / X range</b> — set axis limits manually. Leave blank to
          auto-scale (percentage-like metrics default to 0–100).</li>
      </ul>

      <p class="viz-help-note">Each line is one job — hover any point for its exact value.</p>
    </div>
  </ng-template>

  <div class="viz-canvas">
    @if (tracefits.isLoading()) {
    <div class="viz-loading">Loading trace-fit data…</div>
    } @else if (tracefits.error()) {
    <div class="viz-error">Failed to load trace-fit data: {{ tracefits.error() }}</div>
    } @else {
    <div echarts [options]="chartOptions()" class="seismic-chart"></div>
    }
  </div>

  <div class="viz-meta">
    <!-- Data: what is plotted -->
    <nz-divider class="viz-section-title" nzOrientation="left" nzPlain nzText="Data"></nz-divider>
    <div class="viz-section">
      <div class="viz-pill"><b>View</b>
        <nz-radio-group nzSize="small" nzButtonStyle="solid" [ngModel]="traceType()"
          (ngModelChange)="traceType.set($event)">
          <label nz-radio-button nzValue="iteration">Iteration</label>
          <label nz-radio-button nzValue="spatial">Spatial</label>
        </nz-radio-group>
      </div>

      <div class="viz-pill"><b>Profile</b>
        <nz-select [ngModel]="selectedFit()" (ngModelChange)="selectedFit.set($event)" nzSize="small"
          nzVariant="borderless" style="min-width: 150px; text-align: right;" nzShowSearch
          [nzDropdownMatchSelectWidth]="false">
          @for (option of metricOptions(); track option.value) {
          <nz-option [nzValue]="option.value" [nzLabel]="option.label" />
          }
        </nz-select>
      </div>
    </div>

    <!-- Spatial: aggregation along inline/crossline -->
    @if (traceType() === 'spatial') {
    <nz-divider class="viz-section-title" nzOrientation="left" nzPlain nzText="Spatial"></nz-divider>
    <div class="viz-section">
      <div class="viz-pill"><b>Axis</b>
        <nz-radio-group nzSize="small" nzButtonStyle="solid" [ngModel]="spatialAxis()"
          (ngModelChange)="spatialAxis.set($event)">
          <label nz-radio-button nzValue="inline">Inline</label>
          <label nz-radio-button nzValue="xline">Crossline</label>
        </nz-radio-group>
      </div>

      <div class="viz-pill"><b>Iteration</b>
        <nz-select [ngModel]="resolvedIteration()" (ngModelChange)="selectedSpatialIteration.set($event)"
          nzSize="small" nzVariant="borderless" style="min-width: 80px; text-align: right;"
          [nzDropdownMatchSelectWidth]="false">
          @for (iter of spatialIterations(); track $index) {
          <nz-option [nzValue]="iter" [nzLabel]="iter" />
          }
        </nz-select>
      </div>

      <div class="viz-pill"><b>Window (m)</b>
        <input class="viz-window-input" type="number" min="1" step="1" [ngModel]="windowSize()"
          (ngModelChange)="windowSize.set(+$event)" />
      </div>
    </div>
    }

    <!-- Display: how it is rendered and scaled -->
    <nz-divider class="viz-section-title" nzOrientation="left" nzPlain nzText="Display settings"></nz-divider>
    <div class="viz-section">
      <div class="viz-pill"><b>Type</b>
        <nz-radio-group nzSize="small" nzButtonStyle="solid" [ngModel]="plotType()"
          (ngModelChange)="plotType.set($event)">
          <label nz-radio-button nzValue="line">Line</label>
          <label nz-radio-button nzValue="scatter">Scatter</label>
        </nz-radio-group>
      </div>

      <div class="viz-pill"><b>Y range</b>
        <input class="viz-limit-input" type="number" placeholder="min" [ngModel]="yMin()"
          (ngModelChange)="yMin.set(toLimit($event))" />
        <span class="viz-limit-sep">-</span>
        <input class="viz-limit-input" type="number" placeholder="max" [ngModel]="yMax()"
          (ngModelChange)="yMax.set(toLimit($event))" />
      </div>

      @if (traceType() === 'spatial') {
      <div class="viz-pill"><b>X range</b>
        <input class="viz-limit-input" type="number" placeholder="min" [ngModel]="xMin()"
          (ngModelChange)="xMin.set(toLimit($event))" />
        <span class="viz-limit-sep">-</span>
        <input class="viz-limit-input" type="number" placeholder="max" [ngModel]="xMax()"
          (ngModelChange)="xMax.set(toLimit($event))" />
      </div>
      }
    </div>
  </div>
</div>
}
@else {
<!-- Placeholder: show the raw payload until the real rendering is built out. -->
<pre class="xwai-trace-fit-raw">{{ data() | json }}</pre>
}